<template>
  <div class="gleason-section">
    <div class="section-title" @click="toggleSection('summary')">
      <span>{{ $t('whole-slide-result') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.summary }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.summary" class="section-content">
      <table class="table is-fullwidth centered-table">
        <thead>
          <tr>
            <th>{{ $t('item') }}</th>
            <th>{{ $t('area-mm2') }}</th>
            <th>{{ $t('percentage') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="annotation in result.annotations">
            <tr :key="annotation.label">
              <td>
                <button class="algorithm-term-toggle" type="button" @click="toggleAlgorithmTerm(annotation.label)">
                  <span class="arrow" :class="{ open: isAlgorithmTermExpanded(annotation.label) }">▶</span>
                </button>
                <label class="switch is-small" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isTermVisible(annotation.label)"
                    @change="toggleTermVisibility(annotation.label, annotation)"
                  >
                  <span class="switch-slider"></span>
                </label>
                <span class="dot" :style="{ background: annotation.color }"></span>
                <span class="ml-2 term-label-cap">{{ annotation.label }}</span>
              </td>
              <td class="has-text-centered">{{ annotation.area }}</td>
              <td class="has-text-centered">{{ annotation.percent }}</td>
            </tr>
            <tr v-if="isAlgorithmTermExpanded(annotation.label)" :key="`${annotation.label}-annotations`">
              <td colspan="3" class="algorithm-annotation-row">
                <algorithm-annotation-list
                  :state="algorithmTermState(annotation.label)"
                  :loading="isAlgorithmTermLoading(annotation.label)"
                  :color="algorithmTermColorHex(annotation.label, annotation.color)"
                  :terms="allTerms"
                  :images="images"
                  @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
                  @centerView="$emit('centerView', $event)"
                  @deletion="$emit('deletion', $event)"
                  @select="$emit('select', $event)"
                  @loadMore="requestAlgorithmAnnotations(annotation.label, true)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div class="columns is-mobile is-multiline m-0">
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('primary-pattern') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-primary">{{ result.scoring.primaryPattern }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('secondary-pattern') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-info">{{ result.scoring.secondaryPattern }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('tertiary-pattern') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-warning">{{ result.scoring.tertiaryPattern || '-' }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('tumor-percentage') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-danger">{{ result.scoring.tumorPercentage }}%</div>
          </div>
        </div>
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('gleason-score') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-warning">
              {{ result.scoring.gleasonScoreLabel }} = {{ result.scoring.totalScore }}
            </div>
          </div>
        </div>
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('grade-group') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-danger">{{ result.scoring.gradeGroupLabel }}</div>
          </div>
        </div>
      </div>

      <div class="box p-3 mt-2 scoring-details-box">
        <div class="has-text-grey has-text-weight-semibold mb-2">{{ $t('calculation-details') }}</div>
        <table class="table is-fullwidth is-size-7 centered-table">
          <tbody>
            <tr><td>{{ $t('total-malignant-area-tm') }}</td><td class="has-text-centered">{{ result.scoring.totalMalignantArea }}</td></tr>
            <tr><td>{{ $t('total-classified-area-tc') }}</td><td class="has-text-centered">{{ result.scoring.totalClassifiedArea }}</td></tr>
            <tr><td>{{ $t('malignant-percentage-sum') }}</td><td class="has-text-centered">{{ result.scoring.malignantPercentageSum }}%</td></tr>
            <tr><td>{{ $t('pattern-area-threshold') }}</td><td class="has-text-centered">{{ result.scoring.minPatternAreaThreshold }} mm²</td></tr>
            <tr><td>{{ $t('secondary-tertiary-threshold') }}</td><td class="has-text-centered">{{ result.scoring.patternThresholdPercentage }}%</td></tr>
            <tr><td>{{ $t('calculated-g3-percentage') }}</td><td class="has-text-centered">{{ result.scoring.calculatedPercentages.gleason3 }}%</td></tr>
            <tr><td>{{ $t('calculated-g4-percentage') }}</td><td class="has-text-centered">{{ result.scoring.calculatedPercentages.gleason4 }}%</td></tr>
            <tr><td>{{ $t('calculated-g5-percentage') }}</td><td class="has-text-centered">{{ result.scoring.calculatedPercentages.gleason5 }}%</td></tr>
            <tr><td>{{ $t('calculated-stroma-percentage') }}</td><td class="has-text-centered">{{ result.scoring.calculatedPercentages.stroma }}%</td></tr>
            <tr><td>{{ $t('calculated-benign-percentage') }}</td><td class="has-text-centered">{{ result.scoring.calculatedPercentages.benign }}%</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('report')">
      <span>{{ $t('report') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.report }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.report" class="section-content report-form">
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal"><label class="label mb-0">{{ $t('grade-group') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.gradeGroupLabel">
                <option value="Grade Group 1">{{ $t('grade-group-1') }}</option>
                <option value="Grade Group 2">{{ $t('grade-group-2') }}</option>
                <option value="Grade Group 3">{{ $t('grade-group-3') }}</option>
                <option value="Grade Group 4">{{ $t('grade-group-4') }}</option>
                <option value="Grade Group 5">{{ $t('grade-group-5') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal"><label class="label mb-0">{{ $t('gleason') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.gleasonScoreLabel" @change="syncGleasonScoreFromLabel">
                <option value="3+3">3 + 3</option>
                <option value="3+4">3 + 4</option>
                <option value="4+3">4 + 3</option>
                <option value="4+4">4 + 4</option>
                <option value="3+5">3 + 5</option>
                <option value="5+3">5 + 3</option>
                <option value="4+5">4 + 5</option>
                <option value="5+4">5 + 4</option>
                <option value="5+5">5 + 5</option>
              </select>
            </div>
          </div>
          <div class="control gleason-score-tag-control">
            <span class="tag is-light gleason-score-tag">
              {{ $t('gleason-score') }}: {{ result.report.gleasonScoreLabel ? `${result.report.gleasonScoreLabel} = ${result.report.gleasonScore}` : $t('not-available') }}
            </span>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center" v-for="level in [5, 4, 3]" :key="'g' + level">
        <div class="field-label is-small is-normal"><label class="label mb-0">{{ $t('grade') }} {{ level }} %</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-control-fill">
            <input class="input is-small" type="number" step="0.1" v-model.number="result.report['gleason' + level]">
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal"><label class="label mb-0">{{ $t('cribriform') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.cribriform">
                <option value="Present">{{ $t('present') }}</option>
                <option value="Absent">{{ $t('absent') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal"><label class="label mb-0">{{ $t('tertiary-pattern') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.tertiaryPatternLabel">
                <option value="None">{{ $t('none') }}</option>
                <option value="Pattern 3">{{ $t('pattern-3') }}</option>
                <option value="Pattern 4">{{ $t('pattern-4') }}</option>
                <option value="Pattern 5">{{ $t('pattern-5') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <hr class="my-2 has-background-grey-dark" />
      <div class="is-size-7 has-text-weight-bold mb-2">{{ $t('tumor-quantification') }}</div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('tissue-tumor-percent') }}</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-control-fill">
            <input class="input is-small" type="number" step="0.1" v-model.number="result.report.tumorQuantification">
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('tumor-area-mm2') }}</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-control-fill">
            <input class="input is-small" type="number" step="0.001" v-model.number="result.report.totalMalignantArea" readonly>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('total-classified-area-mm2') }}</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-control-fill">
            <input class="input is-small" type="number" step="0.001" v-model.number="result.report.totalClassifiedArea" readonly>
          </div>
        </div>
      </div>

      <button class="button is-primary is-small is-fullwidth mt-3">{{ $t('confirm-results') }}</button>
    </div>
  </div>
</template>

<script>
import AlgorithmAnnotationList from './AlgorithmAnnotationList';

export default {
  name: 'gleason-result-panel',
  components: {
    AlgorithmAnnotationList
  },
  props: {
    result: {
      type: Object,
      required: true
    },
    project: Object,
    index: String,
    allTerms: {
      type: Array,
      default: () => []
    },
    ontologyTerms: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    toggleSection(sectionName) {
      if (this.result.sectionCollapsed && typeof this.result.sectionCollapsed[sectionName] !== 'undefined') {
        this.result.sectionCollapsed[sectionName] = !this.result.sectionCollapsed[sectionName];
      }
    },
    ensureAlgorithmAnnotationState(termName) {
      if (!this.result.algorithmAnnotations) {
        this.$set(this.result, 'algorithmAnnotations', {});
      }
      if (!this.result.algorithmAnnotations[termName]) {
        this.$set(this.result.algorithmAnnotations, termName, {
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          hasMore: false
        });
      }
      return this.result.algorithmAnnotations[termName];
    },
    algorithmTermState(termName) {
      return this.ensureAlgorithmAnnotationState(termName);
    },
    isAlgorithmTermExpanded(termName) {
      return this.algorithmTermState(termName).expanded;
    },
    isAlgorithmTermLoading(termName) {
      return this.algorithmTermState(termName).loading;
    },
    toggleAlgorithmTerm(termName) {
      const state = this.ensureAlgorithmAnnotationState(termName);
      state.expanded = !state.expanded;
      if (state.expanded && !state.items.length && !state.loading) {
        this.requestAlgorithmAnnotations(termName);
      }
    },
    requestAlgorithmAnnotations(termName, append = false) {
      this.$emit('loadAlgorithmAnnotations', { result: this.result, termName, append });
    },
    isTermVisible(termName) {
      const term = this.allTerms.find(t => t.name === termName);
      if (term && this.ontologyTerms[term.id]) {
        return this.ontologyTerms[term.id].visible;
      }
      return true;
    },
    toggleTermVisibility(termName, annotation) {
      annotation.showOnImage = !annotation.showOnImage;
      this.$emit('toggleTermVisibility', termName);
    },
    algorithmTermColorHex(termName, fallbackColor = null) {
      return (fallbackColor || this.getTermColorByName(termName) || '#1E88E5').replace(/^#/, '');
    },
    getTermColorByName(name) {
      const term = this.allTerms.find(t => t.name === name);
      return term ? term.color : null;
    },
    syncGleasonScoreFromLabel() {
      const label = this.result?.report?.gleasonScoreLabel || '';
      const isValidLabel = /^[3-5]\+[3-5]$/.test(label);
      const parts = isValidLabel ? label.split('+').map(value => Number(value)) : [];
      const isValidScore = parts.length === 2 && parts.every(value => Number.isFinite(value));
      this.result.report.gleasonScore = isValidScore ? parts[0] + parts[1] : 0;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/colors.scss';

.section-title {
  padding: 0.72rem 0.85rem;
  font-weight: 700;
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(222, 238, 255, 0.045);
  color: $dark-text-primary;
  align-items: center;
  margin-top: 8px;
  transition: background-color 0.16s ease;
}

.section-title:hover {
  background-color: rgba($primary, 0.08);
}

.section-title:first-child {
  margin-top: 0;
}

.section-content {
  padding: 0.8rem 0.85rem;
  background-color: rgba(8, 17, 29, 0.18);
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  font-size: 13px;
}

.section-content .table,
.section-content .label {
  font-size: 13px !important;
}

.section-content .input,
.section-content .select select {
  font-size: 13px !important;
  height: 2.25em;
}

.centered-table {
  background-color: transparent !important;
  color: $dark-text-primary !important;
}

.centered-table th {
  color: $dark-text-secondary !important;
  border-color: $dark-border-color !important;
  text-align: center !important;
}

.centered-table td {
  border-color: $dark-border-color !important;
  vertical-align: middle !important;
}

.centered-table tbody tr:hover {
  background-color: rgba($primary, 0.055);
}

.centered-table th:first-child,
.centered-table td:first-child {
  text-align: left !important;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  inset: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked+.switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked+.switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked+.switch-slider:before {
  transform: translateX(16px);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  min-width: 12px;
  min-height: 12px;
}

.term-label-cap {
  text-transform: capitalize;
}

.algorithm-term-toggle {
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  margin-right: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: $dark-text-secondary;
  cursor: pointer;
  vertical-align: middle;
}

.algorithm-term-toggle:hover {
  color: $dark-text-primary;
}

.algorithm-annotation-row {
  padding: 0 !important;
  background: rgba(8, 17, 29, 0.28);
}

.scoring-box,
.scoring-details-box {
  background: rgba(222, 238, 255, 0.038);
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
}

.report-form .field-label {
  text-align: left;
  flex: 0 0 110px;
  max-width: 110px;
  padding-top: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.report-form .field-body {
  flex: 1 1 auto;
  min-width: 0;
  align-items: center !important;
  display: flex !important;
}

.report-form .control {
  display: flex !important;
  align-items: center !important;
  gap: 5px;
}

.report-form .field {
  margin-bottom: 0.65rem !important;
}

.report-form .field-label.report-label-wide {
  flex: 0 0 160px;
  max-width: 160px;
}

.report-form .control.report-control-fill {
  width: 100%;
}

.gleason-score-tag-control {
  margin-left: 12px;
  flex-shrink: 0;
}

.gleason-score-tag {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  min-height: 2em;
}

.align-items-center {
  align-items: center;
}

.d-flex-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
